import { Fragment, useState, type ReactNode } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { usePinnedProjects } from "../features/projects/projectPins";
import { NotificationBell } from "../features/notifications/NotificationBell";
import { WatchlistButton } from "../features/watchlist/WatchlistButton";
import { usePageTitle } from "../app/PageTitleContext";
import {
  CalendarRange,
  BarChart3,
  Boxes,
  CalendarClock,
  CalendarDays,
  CalendarOff,
  ChevronLeft,
  ChevronRight,
  CircleQuestionMark,
  ClipboardCheck,
  ClipboardList,
  Clock,
  FolderKanban,
  Gauge,
  LayoutDashboard,
  ListChecks,
  ListTodo,
  LogOut,
  MonitorSmartphone,
  Moon,
  Plus,
  Rocket,
  Search as SearchIcon,
  Server,
  Shapes,
  ShieldCheck,
  Sun,
  UserSquare2,
  Users,
  Bug,
} from "lucide-react";
import { useAuth } from "../app/AuthContext";
import { toggleTheme, useTheme } from "../app/theme";
import { WorkItemFormDrawer } from "../features/workitems/WorkItemFormDrawer";
import { useCurrentProject } from "../shared/hooks/currentProject";
import { BrandMark, Button, cn } from "../shared/ui";

interface NavItem {
  label: string;
  to: string;
  icon: ReactNode;
  permission?: string;
  /** Belongs to a phase that has not been built yet. */
  phase?: string;
}

/**
 * The §14 navigation, in full.
 *
 * <p>Items belonging to later phases are shown disabled with the phase named, rather than hidden.
 * Hiding them would make the product look smaller than it is and leave people wondering whether a
 * feature exists at all; naming the phase sets the expectation honestly. This is an internal tool
 * whose audience knows it is being built in stages.
 */
const SECTIONS: { heading: string; items: NavItem[] }[] = [
  {
    heading: "Work",
    items: [
      { label: "Home", to: "/", icon: <LayoutDashboard className="h-4 w-4" /> },
      {
        label: "My Work",
        to: "/my-work",
        icon: <ListTodo className="h-4 w-4" />,
        permission: "WORK_ITEM_VIEW",
      },
      {
        label: "Bugs",
        to: "/bugs",
        icon: <Bug className="h-4 w-4" />,
        permission: "WORK_ITEM_VIEW",
      },
      {
        label: "Search",
        to: "/search",
        icon: <SearchIcon className="h-4 w-4" />,
        permission: "WORK_ITEM_VIEW",
      },
      {
        label: "Projects",
        to: "/projects",
        icon: <FolderKanban className="h-4 w-4" />,
        permission: "PROJECT_VIEW",
      },
      {
        label: "Backlog",
        to: "/backlog",
        icon: <ListTodo className="h-4 w-4" />,
        permission: "WORK_ITEM_VIEW",
      },
      {
        label: "Sprints",
        to: "/sprints",
        icon: <Rocket className="h-4 w-4" />,
        permission: "SPRINT_VIEW",
      },
      {
        label: "Release calendar",
        to: "/release-calendar",
        icon: <CalendarRange className="h-4 w-4" />,
        permission: "RELEASE_VIEW",
      },
    ],
  },
  {
    heading: "Organisation",
    items: [
      { label: "Teams", to: "/teams", icon: <Users className="h-4 w-4" />, permission: "TEAM_VIEW" },
      {
        label: "Resources",
        to: "/resources",
        icon: <Gauge className="h-4 w-4" />,
        permission: "RESOURCE_VIEW",
      },
      {
        label: "Leave",
        to: "/leave",
        icon: <CalendarOff className="h-4 w-4" />,
        permission: "LEAVE_VIEW",
      },
      {
        label: "Timesheets",
        to: "/timesheets",
        icon: <Clock className="h-4 w-4" />,
        permission: "TIMESHEET_VIEW",
      },
      {
        label: "Timesheet approvals",
        to: "/timesheets/approvals",
        icon: <ClipboardCheck className="h-4 w-4" />,
        permission: "TIMESHEET_APPROVE",
      },
      {
        label: "Schedule changes",
        to: "/schedule-changes",
        icon: <CalendarClock className="h-4 w-4" />,
        permission: "CHANGE_ANALYTICS_VIEW",
      },
      {
        label: "Reports",
        to: "/reports",
        icon: <BarChart3 className="h-4 w-4" />,
        permission: "REPORT_RESOURCE_VIEW",
      },
    ],
  },
  {
    heading: "Administration",
    items: [
      {
        label: "Employees",
        to: "/admin/employees",
        icon: <UserSquare2 className="h-4 w-4" />,
        permission: "EMPLOYEE_VIEW",
      },
      {
        label: "Roles & Permissions",
        to: "/admin/roles",
        icon: <ShieldCheck className="h-4 w-4" />,
        permission: "EMPLOYEE_VIEW",
      },
      {
        label: "Release Templates",
        to: "/admin/release-templates",
        icon: <ClipboardList className="h-4 w-4" />,
        permission: "RELEASE_TEMPLATE_VIEW",
      },
      {
        label: "Release Fields",
        to: "/admin/release-fields",
        icon: <ListChecks className="h-4 w-4" />,
        permission: "RELEASE_VIEW",
      },
      {
        label: "Modules",
        to: "/admin/modules",
        icon: <Boxes className="h-4 w-4" />,
        permission: "RELEASE_VIEW",
      },
      {
        label: "Module types",
        to: "/admin/module-types",
        icon: <Shapes className="h-4 w-4" />,
        permission: "RELEASE_VIEW",
      },
      {
        label: "Environments",
        to: "/admin/environments",
        icon: <Server className="h-4 w-4" />,
        permission: "RELEASE_VIEW",
      },
      {
        label: "Calendars & Holidays",
        to: "/admin/calendars",
        icon: <CalendarDays className="h-4 w-4" />,
        permission: "CALENDAR_VIEW",
      },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { user, signOut, signOutEverywhere, can } = useAuth();
  const navigate = useNavigate();
  const currentProject = useCurrentProject();
  const page = usePageTitle();
  const [creating, setCreating] = useState(false);
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);

  const initials = (user?.fullName ?? "?")
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="flex h-screen overflow-hidden">
      <aside
        className={cn(
          "flex shrink-0 flex-col border-r border-ink-200 bg-surface transition-[width] duration-200",
          collapsed ? "w-[68px]" : "w-64",
        )}
      >
        <div className="flex h-14 items-center gap-2.5 border-b border-ink-100 px-4">
          <BrandMark className="h-8 w-8 shrink-0 rounded-full" />
          {!collapsed && (
            <span className="font-display truncate text-[15px] font-semibold text-ink-900">
              TaZlo
            </span>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          {SECTIONS.map((section) => {
            const visible = section.items.filter(
              (item) => !item.permission || can(item.permission) || item.phase,
            );
            if (visible.length === 0) {
              // A section whose every item is hidden by permission is dropped entirely, rather
              // than leaving a heading with nothing beneath it.
              return null;
            }

            return (
              <div key={section.heading} className="mb-5">
                {!collapsed && (
                  <p className="px-3 pb-1.5 text-[11px] font-semibold uppercase tracking-wider text-ink-400">
                    {section.heading}
                  </p>
                )}
                <div className="space-y-0.5">
                  {visible.map((item) => {
                    const unbuilt = Boolean(item.phase);
                    const denied = Boolean(item.permission) && !can(item.permission!);
                    const disabled = unbuilt || denied;

                    if (disabled) {
                      return (
                        <div
                          key={item.to}
                          title={unbuilt ? `Arrives in ${item.phase}` : "You do not have access"}
                          className={cn(
                            "flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-ink-300",
                            collapsed && "justify-center px-0",
                          )}
                        >
                          <span className="shrink-0">{item.icon}</span>
                          {!collapsed && (
                            <>
                              <span className="truncate">{item.label}</span>
                              {unbuilt && (
                                <span className="ml-auto shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ink-400 ring-1 ring-inset ring-ink-200">
                                  {item.phase?.replace("Phase ", "P")}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      );
                    }

                    return (
                      <Fragment key={item.to}>
                        <NavLink
                          to={item.to}
                          end={item.to === "/"}
                          title={collapsed ? item.label : undefined}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                              collapsed && "justify-center px-0",
                              isActive
                                ? "bg-brand-600 text-white shadow-sm"
                                : "text-ink-600 hover:bg-ink-100 hover:text-ink-900",
                            )
                          }
                        >
                          <span className="shrink-0">{item.icon}</span>
                          {!collapsed && <span className="truncate">{item.label}</span>}
                        </NavLink>
                        {/* Directly beneath Projects, because that is what they are a shortcut
                            into — at the foot of the section they would read as siblings of
                            Sprints and Backlog. */}
                        {item.to === "/projects" && !collapsed && <PinnedProjectLinks />}
                      </Fragment>
                    );
                  })}

                </div>
              </div>
            );
          })}
        </nav>

        <button
          type="button"
          onClick={() => setCollapsed((value) => !value)}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          className="flex h-11 items-center justify-center gap-2 border-t border-ink-100 text-ink-400 transition-colors hover:bg-ink-50 hover:text-ink-700"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {!collapsed && <span className="text-xs font-medium">Collapse</span>}
        </button>
      </aside>

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="app-header flex h-14 shrink-0 items-center gap-1 border-b border-ink-200 bg-surface px-4">
          {/*
            What this screen is, in the half of the bar that was empty. The page announces it —
            see PageTitleContext — so every screen moved at once rather than one at a time.

            Truncated rather than wrapped: the bar is a fixed fourteen units tall and a long
            sentence that pushed it taller would move every page on the site down by as much as
            the heading block this replaced.
          */}
          <div className="mr-auto min-w-0 flex-1 pr-4">
            {page && (
              <>
                <h1 className="truncate font-display text-base font-semibold text-ink-900">
                  {page.title}
                </h1>
                {page.subtitle && (
                  <p className="truncate text-xs text-ink-500" title={
                    typeof page.subtitle === "string" ? page.subtitle : undefined
                  }>
                    {page.subtitle}
                  </p>
                )}
              </>
            )}
          </div>
          {/*
            The one thing anybody comes here to do that is not reading something. It sits in the
            shell rather than on each screen because the thought "I should raise a bug for that"
            arrives while you are looking at a timesheet as often as a backlog.
          */}
          {can("WORK_ITEM_CREATE") && (
            <Button
              variant="subtle"
              onClick={() => setCreating(true)}
              icon={<Plus className="h-4 w-4" />}
              /*
                A tint rather than a fill. Solid brand is what a page's own primary action wears —
                "New sprint" sits a few pixels below this one — and two solid buttons stacked
                argue about which is the important one. The shell's action reads as the
                application's rather than this screen's.

                The ring, not the fill, is what makes it read as a button. Measured on the header
                the tint is 1.14:1 against it in light and 1.01:1 in dark — brand-50 re-maps to a
                near-black violet that is the same luminance as the surface behind it. So the
                boundary is drawn by brand-400 at 40%, which inverts the right way: darker than
                the tint in light, lighter than it in dark, visible in both.
              */
              className="mr-2 shrink-0 whitespace-nowrap ring-1 ring-inset ring-brand-400"
              title={
                currentProject?.name
                  ? `New work item in ${currentProject.name}`
                  : "New work item"
              }
            >
              New item
            </Button>
          )}
          <NotificationBell />
          <WatchlistButton />
          {/*
            Beside the bell rather than buried in the account menu. Somebody who does not
            understand a screen is looking at the screen, not hunting through their profile —
            and it carries the page they were on, so the guide can say where they are.
          */}
          <button
            type="button"
            onClick={() => navigate("/guide")}
            aria-label="User guide"
            title="User guide"
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
          >
            <CircleQuestionMark className="h-[18px] w-[18px]" />
          </button>
          <button
            type="button"
            onClick={toggleTheme}
            aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            title={theme === "dark" ? "Light mode" : "Dark mode"}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
          >
            {theme === "dark" ? <Sun className="h-[18px] w-[18px]" /> : <Moon className="h-[18px] w-[18px]" />}
          </button>

          {user && (
            <>
              <div className="mx-2 flex shrink-0 items-center gap-2.5">
                <div className="grid h-8 w-8 place-items-center rounded-full bg-brand-50 text-xs font-semibold text-brand-700">
                  {initials}
                </div>
                <div className="hidden leading-tight sm:block">
                  <p className="text-sm font-medium text-ink-900">{user.fullName}</p>
                  <p className="text-xs text-ink-500">{user.designation}</p>
                </div>
              </div>
              {/* Ending every session matters after a lost laptop or a shared machine, so it is
                  offered here rather than buried in a settings screen nobody visits under stress. */}
              <button
                type="button"
                onClick={() => void signOutEverywhere()}
                aria-label="Sign out on every device"
                title="Sign out everywhere"
                className="grid h-9 w-9 place-items-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
              >
                <MonitorSmartphone className="h-[18px] w-[18px]" />
              </button>
              <button
                type="button"
                onClick={() => void signOut()}
                aria-label="Sign out"
                title="Sign out"
                className="grid h-9 w-9 place-items-center rounded-lg text-ink-500 transition-colors hover:bg-ink-100 hover:text-ink-900"
              >
                <LogOut className="h-[18px] w-[18px]" />
              </button>
            </>
          )}
        </header>

        <main className="flex-1 overflow-y-auto bg-ink-50 px-6 py-6">{children}</main>

        {/*
          Keyed on the project so reopening the drawer after moving to another screen starts from
          that screen's project rather than the one it was last opened with.
        */}
        <WorkItemFormDrawer
          key={currentProject?.id ?? "none"}
          open={creating}
          chooseProject
          projectId={currentProject?.id}
          onClose={() => setCreating(false)}
          onSaved={(created) => created && navigate(`/work-items/${created.itemKey}`)}
        />
      </div>
    </div>
  );
}

/**
 * The projects this person pinned, listed under Projects in the sidebar.
 *
 * <p>Indented and quieter than the sections above: these are shortcuts into one section, not
 * sections of their own, and giving them the same weight would make the navigation look twice as
 * long as it is.
 *
 * <p>Renders nothing at all when there are none, rather than an empty heading explaining what
 * pinning is. The place to learn that is the Projects page, where the drop target says so.
 */
function PinnedProjectLinks() {
  const pinned = usePinnedProjects();
  const rows = pinned.data ?? [];

  if (rows.length === 0) {
    return null;
  }

  return (
    <div className="mt-0.5 space-y-0.5 border-l border-ink-200 pl-3 ml-5">
      {rows.map((row) => (
        <NavLink
          key={row.projectId}
          to={`/projects/${row.projectId}`}
          title={row.name}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm transition-colors",
              isActive
                ? "bg-brand-50 font-medium text-brand-700"
                : "text-ink-500 hover:bg-ink-100 hover:text-ink-900",
            )
          }
        >
          <span className="font-mono text-[10px] text-ink-400">{row.projectKey}</span>
          <span className="truncate">{row.name}</span>
        </NavLink>
      ))}
    </div>
  );
}
